<template>
    <div>
        <div
            class="tw-relative tw-text-gray-500 tw-mb-6">
            <span
                class="material-symbols-outlined tw-absolute tw-text-xl tw-leading-none tw-top-1/2 tw-left-3.5 -tw-translate-y-1/2">search</span>
            <input 
                ref="inputAutocompleteAddress"
                @keydown.enter.prevent 
                :id="idSuffix ? 'gm-autocomplete-' + idSuffix : 'gm-autocomplete'" 
                type="search"
                :placeholder="placeholder"
                :class="autocompleteClass ? autocompleteClass : 'tw-w-full placeholder:tw-text-gray-500 tw-p-0 focus-ring-gray tw-py-2.5 tw-px-3.5 tw-pl-[42px] tw-rounded-lg tw-border tw-border-gray-300 tw-shadow-sm'"
                v-model="autocompleteAddress" v-if="showAutocomplete" autocomplete="off">
        </div>
        <div class="tw-rounded-lg" v-if="showMap" :id="idSuffix ? 'gm-map-' + idSuffix : 'gm-map'"></div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { axios } from "lib/Init";
import Geocoder from 'lib/Geocoder';

declare let google: any;

const COMPONENT_FORM_MAP = {
    street_number: 'short_name',
    route: 'long_name',
    locality: 'long_name',
    administrative_area_level_1: 'short_name',
    country: 'short_name',
    postal_code: 'short_name',
    administrative_area_level_3: 'long_name'
};

export default defineComponent({
    name: 'map-component',
    props: {
        "idSuffix": {
            required: false,
            default: false,
            type: String
        },
        "options": {
            required: false,
            default() {
                return {};
            },
            type: Object
        },
        "radius": {
            type: Number,
            required: false,
            default: 100,
        },
        "hideUi": {
            type: Boolean,
            required: false,
            default: false,
        },
        "hideDropdown": {
            type: Boolean,
            required: false,
            default: false
        },
        "showControls": {
            type: Boolean,
            required: false,
            default: false,
        },
        "showAutocomplete": {
            type: Boolean,
            required: false,
            default: true
        },
        "showMap": {
            type: Boolean,
            required: false,
            default: true
        },
        "circle": {
            required: false,
            default() {
                return {
                    strokeColor: '#7fbdec',
                    strokeOpacity: 0.8,
                    strokeWeight: 1,
                    fillColor: '#7fbdec',
                    fillOpacity: 0.35,
                };
            },
            type: Object
        },
        "initialCoordinates": {
            required: false,
            default() {
                return {
                    lat: 43.711140,
                    lng: -79.568235
                };
            },
            type: Object
        },
        "initialAddress": {
            required: false,
            default: "",
            type: String
        },
        "placeholder": {
            required: false,
            default: "Enter your address",
            type: String
        },
        "business": {
            required: false,
            default: false,
            type: Boolean
        },
        "autocompleteClass": {
            required: false,
            default: false,
            type: Boolean
        },
        "shouldFocus":{
            required: false,
            default: false,
            type: Boolean,
        }
    },
    data() {
        return {
            autocompleteAddress: "",
            address: {
                street_number: null,
                route: null,
                locality: null,
                administrative_area_level_1: null,
                administrative_area_level_2: null,
                administrative_area_level_3: null, // The reason to add this is while Testing with (Caledon, ON), the city does not come as locality, it comes on admin area level 3 instead
                country: null,
                postal_code: null
            },
            coordinates: {
                lat: 43.7109,
                lng: -79.5681
            },
            zoom: 9,
            googleMapsLoaded: false
        }
    },
    created() {
        this._dropdownHidden = false;
        this._autocompleteComponent = null;
        this._mapComponent = null;
        this._centerMarker = null;
        this._circleComponent = null;
        this._geocoder = null;
    },
    mounted() {
        this.checkGoogleMapsLoaded(); // Call this method when the component is mounted
        this.shouldFocus && this.$refs.inputAutocompleteAddress.focus();
    },
    computed: {
        searchRadius() {
            return this.radius * 1000
        }
    },
    watch: {
        googleMapsLoaded(loaded) {
            if (loaded) {
                this.initializeComponents(); // Initialize map and autocomplete when Google Maps is loaded
            }
        },
        radius(radius) {
            switch (true) {
                case radius > 50:
                    this.zoom = 8;
                    break;
                case radius <= 50 && radius > 30:
                    this.zoom = 9;
                    break;
                case radius <= 30 && radius > 15:
                    this.zoom = 10;
                    break;
                case radius <= 15:
                    this.zoom = 11;
                    break;
            }
            this.setRadiusCircle(this.coordinates, radius * 1000);
        },
        initialAddress: function (address: string) {
            this.autocompleteAddress = address;
        },
        initialCoordinates: function (coords: object) {
            coords && this.setLocationWithCoordinates(coords);
        }
    },
    methods: {
        checkGoogleMapsLoaded() {
            // @ts-expect-error - Because Map API is loaded Asynchronously, ts might be unaware of window.google changes
            if (window.google?.maps) {
                this.googleMapsLoaded = true; // Set to true if Google Maps is already loaded
            } else {
                setTimeout(this.checkGoogleMapsLoaded, 100); // Retry after a short delay if not loaded yet
            }
        },
        initializeComponents() {
            if(this.initialCoordinates) {
                this.coordinates = {
                    lat: parseFloat(this.initialCoordinates.lat),
                    lng: parseFloat(this.initialCoordinates.lng),
                }
            }
            this.autocompleteAddress = this.initialAddress || "";
            if (this.showMap) {
                this.initMap();
            }
            if (this.showAutocomplete) {
                this.initAutocomplete();
            }
            this.initGeocoder();
        },
        focusInput() {
            this.$refs.addressInput.focus();
        },
        initGeocoder() {
            return this._geocoder = new Geocoder();
        },
        initMap() {
            const componentOptions = {
                center: this.coordinates,
                zoom: this.zoom,
                minZoom: this.zoom,
                maxZoom: this.zoom + 3,
                mapTypeControl: this.showControls,
                disableDefaultUI: this.hideUi,
                zoomControlOptions: {
                    style: google.maps.ZoomControlStyle.tiny
                }
            };

            const options = {
                ...this.options,
                ...componentOptions
            };

            this._mapComponent = new google.maps.Map(document.getElementById(this.idSuffix ? `gm-map-${this.idSuffix}` : 'gm-map'), options);
            this._mapComponent.addListener('click', this.handleMapClick);

            this.pollForBoundsChange();
            this.setCenterMarker();
            this.setRadiusCircle();
        },
        initAutocomplete() {
            // Create the autocomplete object, restricting the search predictions to
            // geographical location types.
            const types = ['geocode'];
            if(this.business) {
                types.push("establishment");
            }
            this._autocompleteComponent = new google.maps.places.Autocomplete(
                document.getElementById(this.idSuffix ? `gm-autocomplete-${this.idSuffix}` : 'gm-autocomplete'),
                {
                  types,
                  componentRestrictions: {
                    country: [
                      'US',
                      'CA'
                    ]
                  }
                }
            );

            // Avoid paying for data that you don't need by restricting the set of
            // place fields that are returned to just the address components.
            const fields = ['address_components', 'geometry'];
            if(this.business) {
                fields.push("place_id");
                fields.push("opening_hours");
                fields.push("formatted_address");
            }
            this._autocompleteComponent.setFields(fields);

            // When the user selects an address from the drop-down, populate the
            // address fields in the form.
            this._autocompleteComponent.addListener('place_changed', this.placeDidChange);
        },
        pollForBoundsChange() {
            google.maps.event.addListenerOnce(this._mapComponent, "bounds_changed", this.boundsDidChange);
        },
        pollForZoomChange() {
            google.maps.event.addListenerOnce(this._mapComponent, "zoom_changed", () => {
                this.pollForBoundsChange();
            });
        },
        hideAutocompleteDropdown() {
            const pacContainer = <HTMLElement>document.querySelector('.pac-container');
            pacContainer.style.display = 'none';
            this._dropdownHidden = true;
        },
        handleMapClick(click) {
            const coordinates = {
                lat: click.latLng.lat(),
                lng: click.latLng.lng()
            };
            this.setLocationWithCoordinates(coordinates);
        },
        placeDidChange(place) {
            if(this.hideDropdown && !this._dropdownHidden) {
                this.hideAutocompleteDropdown();
            }

            this.resetAddress();

            if (!place) {
                place = this._autocompleteComponent.getPlace();
            }

            if (!place.geometry) {
                if (place.name) {
                    this.setLocationWithName(place.name);
                } else {
                    this.report(`Input: ${this.autocompleteAddress}`);
                    console.warn("AutocompleteService: Could not resolve place details.");
                }
                return;
            }

            this.coordinates.lat = place.geometry.location.lat();
            this.coordinates.lng = place.geometry.location.lng();

            this.fillInAddress(place.address_components);
            if (this.showMap) {
                this.centerDidChange();
            }

            const location = {address: this.address, coordinates: this.coordinates};
            if(this.business) {
                location["openingHours"] = place.opening_hours;
                location["formattedAddress"] = place.formatted_address;
                location["placeID"] = place.place_id;
            }

            this.$emit('location-did-change', location, this.autocompleteAddress);
        },
        setRadiusCircle(coordinates, radius) {
            coordinates = coordinates ? coordinates : this.coordinates;
            radius = radius ? radius : this.searchRadius;

            if (!this._circleComponent) {
                this._circleComponent = new google.maps.Circle({
                    strokeColor: this.circle.strokeColor,
                    strokeOpacity: this.circle.strokeOpacity,
                    strokeWeight: this.circle.strokeWeight,
                    fillColor: this.circle.fillColor,
                    fillOpacity: this.circle.fillOpacity,
                    map: this._mapComponent,
                    center: coordinates,
                    radius
                });
                this._circleComponent.addListener('click', this.handleMapClick);
            } else {
                this._circleComponent.setCenter(coordinates);
                this._circleComponent.setRadius(radius);
            }
            this.setViewportBounds();
        },
        setCenterMarker(coordinates) {
            coordinates = coordinates ? coordinates : this.coordinates;

            if (!this._centerMarker) {
                this._centerMarker = new google.maps.Marker({
                    position: coordinates,
                    map: this._mapComponent,
                });
            } else {
                this._centerMarker.setPosition(new google.maps.LatLng(coordinates.lat, coordinates.lng));
            }

            return this._centerMarker;
        },
        centerDidChange(coordinates) {
            coordinates = coordinates ? coordinates : this.coordinates;

            this._mapComponent.setCenter(coordinates);
            this.setCenterMarker(coordinates);

            this.setRadiusCircle();
        },
        boundsDidChange() {
            this.pollForZoomChange();
        },
        setViewportBounds() {
            this._mapComponent.fitBounds(this._circleComponent.getBounds());
        },
        fillInAddress(addressComponents) {
            // Get each component of the address from the place details,
            // and then fill-in the corresponding field on the form.
          for (let i = 0; i < addressComponents.length; i++) {
            const addressType = addressComponents[i].types[0];
                if (COMPONENT_FORM_MAP[addressType]) {
                    const val = addressComponents[i][COMPONENT_FORM_MAP[addressType]];
                    this.address[addressType] = val;
                }
            }
        },
        // Bias the autocomplete object to the user's geographical location,
        // as supplied by the browser's 'navigator.geolocation' object.
        setBias(coordinates, biasRadius = 10000) {
            coordinates = {
                lat: parseFloat(coordinates.lat),
                lng: parseFloat(coordinates.lng),
            }
            const circle = new google.maps.Circle({
                center: coordinates,
                radius: biasRadius
            });
            this._autocompleteComponent.setBounds(circle.getBounds());
        },
        setLocationWithName(name) {
            this.loadPlaceByGeocode({'address': name});
        },
        setLocationWithCoordinates(coordinates) {
            this.loadPlaceByGeocode({'location': coordinates});
        },
        loadPlaceByGeocode(criteria) {
            this._geocoder.geocode(criteria, (results, status) => {
                if (status === 'OK') {
                    if (results[0]) {
                        this.autocompleteAddress = results[0].formatted_address;
                        this.placeDidChange(results[0]);
                    } else {
                       this.report(`Input: ${this.currentInput()}, Geocode Criteria: ${JSON.stringify(criteria)}`);
                        console.warn("Geocoder: No results found.");
                    }
                } else {
                    this.report(`Input: ${this.currentInput()}, Geocode Criteria: ${JSON.stringify(criteria)}`);
                    console.warn(`Geocoder: No results found. Status: ${status}`);
                }
            });
        },
        report(message) {
            axios.post("/api/issue/report", {
                "issue_id": "GM_NO_PLACE",
                message
            });
        },
        currentInput() {
            return (<HTMLInputElement>document.getElementById(this.idSuffix ? `gm-autocomplete-${this.idSuffix}` : 'gm-autocomplete')).value;
        },
        resetAddress() {
            this.address.street_number = null;
            this.address.route = null;
            this.address.locality = null;
            this.address.administrative_area_level_1 = null;
            this.address.administrative_area_level_2 = null;
            this.address.administrative_area_level_3 = null;
            this.address.country = null;
            this.address.postal_code = null;
            this.coordinates.lat = null;
            this.coordinates.lng = null;
        }
    }
});
</script>
